import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import { CRMProvider, useCRM } from "./context/CRMContext";
import Sidebar from "./components/layout/Sidebar";
import ImpersonationBanner from "./components/layout/ImpersonationBanner";
import Login from "./pages/Login/Login";
import Dashboard from "./pages/Dashboard/Dashboard";
import TotalLeads from "./pages/Leads/subpages/TotalLeads";
import FreshLeads from "./pages/Leads/subpages/FreshLeads";
import TodayLeads from "./pages/Leads/subpages/TodayLeads";
import ImportLeads from "./pages/Leads/subpages/ImportLeads";
import AssignLeads from "./pages/Leads/subpages/AssignLeads";
import NonAssignLeads from "./pages/Leads/subpages/NonAssignLeads";
import LockedLeads from "./pages/Leads/subpages/LockedLeads";
import AddLead from "./pages/Leads/subpages/AddLead";
import StatusWiseLeads from "./pages/Leads/subpages/StatusWiseLeads";
import SourceLeads from "./pages/Leads/subpages/SourceLeads";
import Brokers from "./pages/Brokers/Brokers";
import AddBroker from "./pages/Brokers/AddBroker";
import DynamicFields from "./pages/DynamicFields/DynamicFields";
import AllUsers from "./pages/Users/AllUsers";
import AddUser from "./pages/Users/AddUser";
import FollowupPage from "./pages/Followup/FollowupPage";
import Team from "./pages/Team/Team";
import Attendance from "./pages/Attendance/Attendance";
import Loans from "./pages/Loans/Loans";
import AddLoan from "./pages/Loans/AddLoan";
import AllProjects from "./pages/Projects/AllProjects";
import AddProject from "./pages/Projects/AddProject";
import ViewProject from "./pages/Projects/ViewProject";
import AddProperty from "./pages/Properties/AddProperty";
import Properties from "./pages/Properties/Properties";
import ViewProperty from './pages/Properties/ViewProperty';
import "./theme.css";
import "./components/common/Common.css";
import AllCandidates from "./pages/HR/AllCandidates";
import AddCandidate from "./pages/HR/AddCandidate";
import SharedView from "./pages/SharedView/SharedView";
import Integration from "./pages/Integration/Integration";
import TrackUser from "./pages/TrackUser/TrackUser";
import PaySlip from "./pages/PaySlip/PaySlip";
import ChangePassword from "./pages/ChangePassword/ChangePassword";
import Profile from "./pages/Profile/Profile";
import Settings from "./pages/Settings/Settings";
import HelpCenter from "./pages/HelpCenter/HelpCenter";
import LeadsReport from "./pages/LeadsReport/LeadsReport";
import LeadScheduling from "./pages/LeadScheduling/LeadScheduling";
import RecycleLeads from "./pages/RecycleLeads/RecycleLeads";
import RecycleLeadDetail from "./pages/RecycleLeads/RecycleLeadDetail";
import UserLeadsReport from "./pages/LeadsReport/UserLeadsReport";
import { ThemeProvider } from "./context/ThemeContext";
import "./App.css";
import "./responsive.css";

const Guarded = ({ perm, adminOnly, children }) => {
  const { hasPermission, isAdmin } = useAuth();
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (perm && !hasPermission(perm)) return <Navigate to="/" replace />;
  return children;
};

function AppContent() {
  const { sidebarOpen } = useCRM();
  return (
    <div className="app">
      <Sidebar />
      <main
        className={`app__main ${sidebarOpen ? 'app__main--sidebar-open' : 'app__main--sidebar-collapsed'}`}
      >
        <ImpersonationBanner />

        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/leads" element={<Guarded perm="leads.view"><TotalLeads /></Guarded>} />
          <Route path="/leads/fresh" element={<Guarded perm="leads.view"><FreshLeads /></Guarded>} />
          <Route path="/leads/today" element={<Guarded perm="leads.view"><TodayLeads /></Guarded>} />
          <Route path="/leads/imported" element={<Guarded perm="leads.view"><ImportLeads /></Guarded>} />
          <Route path="/leads/assigned" element={<Guarded perm="leads.assign"><AssignLeads /></Guarded>} />
          <Route path="/leads/non-assigned" element={<Guarded perm="leads.assign"><NonAssignLeads /></Guarded>} />
          <Route path="/leads/locked" element={<Guarded perm="leads.view"><LockedLeads /></Guarded>} />
          <Route path="/leads/add" element={<Guarded perm="leads.add"><AddLead /></Guarded>} />
          <Route path="/leads/edit/:id" element={<Guarded perm="leads.edit"><AddLead /></Guarded>} />
          <Route path="/leads/status-wise" element={<Guarded perm="leads.view"><StatusWiseLeads /></Guarded>} />
          <Route path="/leads/source/:srcId" element={<Guarded perm="leads.view"><SourceLeads /></Guarded>} />
          <Route path="/lead-scheduling" element={<Guarded adminOnly><LeadScheduling /></Guarded>} />
          <Route path="/recycle-leads" element={<Guarded adminOnly><RecycleLeads /></Guarded>} />
          <Route path="/recycle-leads/:id" element={<Guarded adminOnly><RecycleLeadDetail /></Guarded>} />
          <Route path="/projects" element={<AllProjects />} />
          <Route path="/projects/add" element={<AddProject />} />
          <Route path="/projects/edit/:id" element={<AddProject />} />
          <Route path="/projects/view/:id" element={<ViewProject />} />
          <Route path="/properties/all" element={<Properties />} />
          <Route path="/properties/add" element={<AddProperty />} />
          <Route path="/properties/edit/:id" element={<AddProperty />} />
          <Route path="/properties/view/:id" element={<ViewProperty />} />
          <Route path="/brokers" element={<Guarded perm="brokers.view"><Brokers /></Guarded>} />
          <Route path="/brokers/list" element={<Guarded perm="brokers.view"><Brokers /></Guarded>} />
          <Route path="/brokers/add" element={<Guarded perm="brokers.add"><AddBroker /></Guarded>} />
          <Route path="/brokers/edit/:id" element={<Guarded perm="brokers.edit"><AddBroker /></Guarded>} />
          <Route path="/dynamic-fields" element={<Guarded perm="dynamic_fields.view"><DynamicFields /></Guarded>} />
          <Route path="/dynamic-fields/manager" element={<Guarded perm="dynamic_fields.view"><DynamicFields /></Guarded>} />
          <Route path="/followup" element={<Navigate to="/followup/today" replace />} />
          <Route path="/followup/today" element={<FollowupPage />} />
          <Route path="/followup/missed" element={<FollowupPage />} />
          <Route path="/followup/upcoming" element={<FollowupPage />} />
          <Route path="/users/all" element={<Guarded adminOnly><AllUsers /></Guarded>} />
          <Route path="/users/add" element={<Guarded adminOnly><AddUser /></Guarded>} />
          <Route path="/users/edit/:id" element={<Guarded adminOnly><AddUser /></Guarded>} />
          <Route path="/hr/candidates" element={<Guarded perm="hr.view"><AllCandidates /></Guarded>} />
          <Route path="/hr/candidates/add" element={<Guarded perm="hr.add"><AddCandidate /></Guarded>} />
          <Route path="/hr/candidates/edit/:id" element={<Guarded perm="hr.edit"><AddCandidate /></Guarded>} />
          <Route path="/team" element={<Team />} />
          <Route path="/attendance" element={<Guarded perm="reports.view"><Attendance /></Guarded>} />
          <Route path="/attendance/:tab" element={<Guarded perm="reports.view"><Attendance /></Guarded>} />
          <Route path="/loans" element={<Guarded perm="loans.view"><Loans /></Guarded>} />
          <Route path="/loans/add" element={<Guarded perm="loans.add"><AddLoan /></Guarded>} />
          <Route path="/loans/edit/:id" element={<Guarded perm="loans.edit"><AddLoan /></Guarded>} />
          <Route path="/api-integration" element={<Integration />} />
          <Route path="/track-team" element={<Guarded perm="users.view_location"><TrackUser /></Guarded>} />
          <Route path="/payslip" element={<PaySlip />} />
          <Route path="/change-password" element={<Guarded perm="settings.change_password"><ChangePassword /></Guarded>} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/leads-report" element={<Guarded perm="reports.view"><LeadsReport /></Guarded>} />
          <Route path="/leads-report/:userId" element={<Guarded perm="reports.view"><UserLeadsReport /></Guarded>} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
    </div>
  );
}

function AuthGate() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#fff' }}>
        Loading...
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <CRMProvider>
      <AppContent />
    </CRMProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <Routes>
              <Route path="/shared/:token" element={<SharedView />} />
              <Route path="/*" element={<AuthGate />} />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
